import type{ Logger } from "../types/logger";
import type { EncodedData } from "./demuxerTypes";
import type { MpegTsDemuxMainToWorkerMessage, MpegTsDemuxMainToWorkerRequest, MpegTsDemuxWorkerToMainMessage, MpegTsDemuxWorkerToMainResponse } from "./workerProtocol";

export interface WorkerTsDemuxer {
  demuxTs(bytes: Uint8Array): Promise<EncodedData>;
  handleMessage(message: MpegTsDemuxWorkerToMainMessage): void,
  close(): void;
}

export function createWorkerTsDemuxerMainHandler(
  services: {
    logger: Logger,
  },
  postMessage: (message: MpegTsDemuxMainToWorkerMessage, transfer: Transferable[]) => void,
  terminate: () => void,
): WorkerTsDemuxer {
  const pendingRequests = new Map<number, PendingRequest>();
  let currentRequestId = 0;

  const handleResponse = (requestId: number, response: MpegTsDemuxWorkerToMainResponse): void => {
    const pendingRequest = pendingRequests.get(requestId);
    pendingRequests.delete(requestId);
    if (pendingRequest === undefined) {
      services.logger.warn(`Unexpected response with requestId=${requestId}`);
      return;
    }
    if (response.type === 'error') {
      pendingRequest.reject(new Error(response.error));
    }
    pendingRequest.resolve(response);
  };

  const postRequest = async (request: MpegTsDemuxMainToWorkerRequest, transfer: Transferable[]): Promise<MpegTsDemuxWorkerToMainResponse> => {
    const requestId = currentRequestId ++;
    return new Promise<MpegTsDemuxWorkerToMainResponse>((resolve, reject) => {
      postMessage({ type: 'request', requestId, request }, transfer);
      pendingRequests.set(
        requestId,
        {
          resolve: (response) => resolve(response),
          reject: (error) => reject(error),
        }
      );
    });
  };


  return {
    async demuxTs(bytes: Uint8Array): Promise<EncodedData> {
      const response = await postRequest({ type: 'demux-ts', bytes }, [bytes.buffer]);
      if (response.type !== 'encoded-data') {
        throw new Error(`Unexpected response from worker: ${response.type}`);
      }

      return response.encodedData;
    },
    handleMessage(message: MpegTsDemuxWorkerToMainMessage): void {
      switch (message.type) {
        case 'response': {
          handleResponse(message.requestId, message.response);
          break;
        }
        default:
          message.type satisfies never;
      }
    },
    close(): void {
      terminate();
    }
  };
}

interface PendingRequest {
  resolve: (response: MpegTsDemuxWorkerToMainResponse) => void,
  reject: (err: unknown) => void,
}

